import { useSearchParams } from 'react-router-dom'
import { LanguageTermsSettings } from '@/components/terminology/LanguageTermsSettings'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CONFIGURE_TABS, configureTabAccess, type ConfigTab } from '@/lib/pageTabs'
import { useTabAccess } from '@/hooks/useTabAccess'
import { UsersPage } from '@/pages/UsersPage'
import { RolesPage } from '@/pages/RolesPage'
import { OptionSetsSettings } from '@/components/admin/OptionSetsSettings'
import { CustomersSettings } from '@/components/admin/CustomersSettings'
import { FeatureFlagsSettings } from '@/components/admin/FeatureFlagsSettings'
import { TenantsSettings } from '@/components/admin/TenantsSettings'
import { ProductsSettings } from '@/components/admin/ProductsSettings'
import { ProductCodesSettings } from '@/components/admin/ProductCodesSettings'
import { DinConfigureSettings } from '@/components/admin/DinConfigureSettings'
import { GeneralSettings } from '@/components/configure/GeneralSettings'
import { LisStagesSettings } from '@/components/admin/LisStagesSettings'
import { MethodSettings } from '@/components/admin/MethodSettings'
import { UserGroupsSettings } from '@/components/admin/UserGroupsSettings'
import { PageHeading } from '@/components/layout/PageHeading'
import { SsoSetupPage } from '@/pages/SsoSetupPage'
import { PoTemplateSettings } from '@/components/configure/po-template/PoTemplateSettings'

const DEFAULT_TAB: ConfigTab = 'general'

// Tabs that were renamed: a link shared under the old name still lands.
// Partners was Customers until z8nrz7earp.
const TAB_ALIASES: Partial<Record<string, ConfigTab>> = { customers: 'partners' }

export function ConfigurePage() {
  // Each tab's gate lives in src/lib/pageTabs.ts, shared with the main menu so
  // the menu never offers a tab this page would refuse (z8nrz7f7g3).
  const allowed = configureTabAccess(useTabAccess())

  // Persist the active tab in the URL (?tab=) so it survives reloads and is shareable.
  const [params, setParams] = useSearchParams()
  const rawParam = params.get('tab')
  const param = (rawParam && TAB_ALIASES[rawParam]) || rawParam
  const requested: ConfigTab = CONFIGURE_TABS.some((t) => t.value === param)
    ? (param as ConfigTab)
    : DEFAULT_TAB
  // Don't land on a tab the user can't see.
  const tab: ConfigTab = allowed[requested] ? requested : DEFAULT_TAB

  function setTab(next: string) {
    const sp = new URLSearchParams(params)
    sp.set('tab', next)
    // `view` only means anything under Method, where it picks between the
    // Procedures and Methods grids. Dropped on the way out so a stale
    // ?tab=users&view=methods can never linger in a shared URL — the same
    // housekeeping IntegrationsPage does for its `sim`.
    if (next !== 'method') sp.delete('view')
    setParams(sp, { replace: true })
  }

  return (
    <div className='flex w-full flex-col gap-6 px-10 py-6'>
      <PageHeading title='Configure' description='Application configuration for your organization.' />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {CONFIGURE_TABS.filter((t) => allowed[t.value]).map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='general' className='mt-6'>
          <p className='mb-6 text-sm text-muted-foreground'>
            General settings for your organization.
          </p>
          <GeneralSettings />
        </TabsContent>

        {allowed.users && (
          <TabsContent value='users' className='mt-6'>
            <UsersPage />
          </TabsContent>
        )}

        {allowed.roles && (
          <TabsContent value='roles' className='mt-6'>
            <RolesPage />
          </TabsContent>
        )}

        {allowed.tenants && (
          <TabsContent value='tenants' className='mt-6'>
            <TenantsSettings />
          </TabsContent>
        )}

        {allowed.partners && (
          <TabsContent value='partners' className='mt-6'>
            <CustomersSettings />
          </TabsContent>
        )}

        {allowed['user-groups'] && (
          <TabsContent value='user-groups' className='mt-6'>
            <p className='mb-6 text-sm text-muted-foreground'>
              Named groups of people. Restrict who may sign a field, or complete a form, to one or
              more groups in the form builder.
            </p>
            <UserGroupsSettings />
          </TabsContent>
        )}

        {allowed['option-sets'] && (
          <TabsContent value='option-sets' className='mt-6'>
            <OptionSetsSettings />
          </TabsContent>
        )}

        {allowed['feature-flags'] && (
          <TabsContent value='feature-flags' className='mt-6'>
            <FeatureFlagsSettings />
          </TabsContent>
        )}

        {allowed.products && (
          <TabsContent value='products' className='mt-6'>
            <ProductsSettings />
          </TabsContent>
        )}

        {allowed['product-code'] && (
          <TabsContent value='product-code' className='mt-6'>
            <ProductCodesSettings />
          </TabsContent>
        )}

        {allowed['din-configure'] && (
          <TabsContent value='din-configure' className='mt-6'>
            <DinConfigureSettings />
          </TabsContent>
        )}

        {allowed['lis-stages'] && (

          <TabsContent value='lis-stages' className='mt-6'>

            <p className='mb-6 text-sm text-muted-foreground'>

              The stages laboratory tests are ordered at, which tests each one orders, and which

              stages must have results back before a later one can be ordered.

            </p>

            <LisStagesSettings />

          </TabsContent>

        )}


        <TabsContent value='language' className='mt-6'>
          <LanguageTermsSettings />
        </TabsContent>

        {allowed.method && (
          <TabsContent value='method' className='mt-6'>
            <MethodSettings />
          </TabsContent>
        )}

        {allowed['purchase-orders'] && (
          <TabsContent value='purchase-orders' className='mt-6'>
            <PoTemplateSettings />
          </TabsContent>
        )}

        {allowed.sso && (
          <TabsContent value='sso' className='mt-6'>
            <SsoSetupPage />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
